import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '../components/ui/sheet';
import { toast } from 'sonner';
import {
  Banknote, ExternalLink, Wallet, Loader2, CheckCircle2, XCircle, Hash,
} from 'lucide-react';

const inr = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usdt = (v) => `${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} USDT`;
const explorer = (h) => `https://tronscan.org/#/transaction/${h}`;

const STATUS = {
  pending_approval: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  approved: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
  processing: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
  sent: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
  completed: 'bg-green-500/15 text-green-600 border-green-500/30',
  rejected: 'bg-red-500/15 text-red-600 border-red-500/30',
  failed: 'bg-red-500/15 text-red-600 border-red-500/30',
};
const Pill = ({ s }) => <Badge variant="outline" className={`rounded-sm text-[10px] ${STATUS[s] || ''}`}>{(s || '').replace(/_/g, ' ').toUpperCase()}</Badge>;

export default function AdminResellerWithdrawalsPage() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);      // {withdrawal, reseller, wallet}
  const [reason, setReason] = useState('');
  const [txHash, setTxHash] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/reseller-withdrawals?status=${filter}`);
      setRows(r.data.withdrawals || []);
      setCounts(r.data.counts || {});
    } catch { toast.error('Failed to load withdrawals'); }
    finally { setLoading(false); }
  }, [api, filter]);
  useEffect(() => { load(); }, [load]);

  const openReview = async (id) => {
    setReason(''); setTxHash('');
    try {
      const r = await api.get(`/admin/reseller-withdrawals/${id}`);
      setSel(r.data); setOpen(true);
    } catch { toast.error('Failed to load withdrawal'); }
  };

  const act = async (path, body, okMsg) => {
    setBusy(true);
    try {
      await api.post(`/admin/reseller-withdrawals/${sel.withdrawal.id}/${path}`, body);
      toast.success(okMsg);
      // After Approve, keep the drawer open and transition in-place to the TX-hash step.
      if (path === 'approve') {
        const r = await api.get(`/admin/reseller-withdrawals/${sel.withdrawal.id}`);
        setSel(r.data); setReason(''); setTxHash('');
      } else {
        setOpen(false);
      }
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const tiles = [
    { key: 'pending_approval', label: 'Pending', tone: 'text-amber-600' },
    { key: 'approved', label: 'In Progress', tone: 'text-blue-600' },
    { key: 'completed', label: 'Completed', tone: 'text-green-600' },
    { key: 'rejected', label: 'Rejected', tone: 'text-red-600' },
  ];

  const w = sel?.withdrawal;
  const isPending = w?.status === 'pending_approval';
  const isApproved = ['approved', 'processing', 'sent'].includes(w?.status);

  return (
    <div className="space-y-6" data-testid="admin-reseller-withdrawals-page">
      <div>
        <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><Banknote className="w-6 h-6" />Reseller Withdrawals</h1>
        <p className="text-muted-foreground text-sm">Review and settle reseller USDT withdrawal requests.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <Card key={t.key} data-testid={`wd-count-${t.key}`}><CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{t.label}</p>
            <p className={`text-2xl font-bold font-heading mt-1 ${t.tone}`}>{counts[t.key] ?? 0}</p>
          </CardContent></Card>
        ))}
      </div>

      <div className="flex justify-end">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px] rounded-sm" data-testid="wd-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending_approval">Pending Approval</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-3 px-4">ID</th>
                <th className="py-3 px-4">Reseller</th>
                <th className="py-3 px-4">USDT</th>
                <th className="py-3 px-4">INR Value</th>
                <th className="py-3 px-4">Address</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Date</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? [...Array(4)].map((_, i) => (
                <tr key={i} className="border-b">{[...Array(8)].map((_, j) => <td key={j} className="py-3 px-4"><Skeleton className="h-4 w-full" /></td>)}</tr>
              )) : rows.length ? rows.map((x) => (
                <tr key={x.id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`admin-wd-row-${x.withdrawal_code}`}>
                  <td className="py-3 px-4 font-mono text-xs font-semibold text-primary">{x.withdrawal_code}</td>
                  <td className="py-3 px-4"><div className="font-medium">{x.reseller_name}</div><div className="text-xs text-muted-foreground font-mono">{x.reseller_code}</div></td>
                  <td className="py-3 px-4 font-mono">{usdt(x.amount_usdt)}</td>
                  <td className="py-3 px-4 font-mono">{inr(x.amount_inr)}</td>
                  <td className="py-3 px-4 font-mono text-xs truncate max-w-[140px]">{x.destination_address}</td>
                  <td className="py-3 px-4"><Pill s={x.status} /></td>
                  <td className="py-3 px-4 text-xs">{new Date(x.created_at).toLocaleDateString('en-IN')}</td>
                  <td className="py-3 px-4 text-right">
                    <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" onClick={() => openReview(x.id)} data-testid={`review-${x.withdrawal_code}`}>Review</Button>
                  </td>
                </tr>
              )) : <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">No withdrawals</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Review drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="wd-review-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">Withdrawal <span className="font-mono text-primary text-sm">{w?.withdrawal_code}</span></SheetTitle>
            <SheetDescription>Review the request details and settle below.</SheetDescription>
          </SheetHeader>
          {w && <div className="pt-1"><Pill s={w.status} /></div>}
          {sel && (
            <div className="space-y-5 py-4 text-sm">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Reseller</p>
                <p className="font-medium">{sel.reseller?.business_name}</p>
                <p className="text-xs text-muted-foreground font-mono">{sel.reseller?.reseller_code} · {sel.reseller?.email}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-muted-foreground">Requested</p><p className="font-mono font-semibold">{usdt(w.amount_usdt)}</p></div>
                <div><p className="text-xs text-muted-foreground">INR Value</p><p className="font-mono font-semibold">{inr(w.amount_inr)}</p></div>
                <div><p className="text-xs text-muted-foreground">Rate (snapshot)</p><p className="font-mono">{inr(w.exchange_rate)}</p></div>
                <div><p className="text-xs text-muted-foreground flex items-center gap-1"><Wallet className="h-3 w-3" />Available</p><p className="font-mono">{inr(sel.wallet?.available_inr)}</p></div>
                {w.fee_usdt > 0 && <div><p className="text-xs text-muted-foreground">Fee</p><p className="font-mono">{usdt(w.fee_usdt)}</p></div>}
                {w.fee_usdt > 0 && <div><p className="text-xs text-muted-foreground">Net to Reseller</p><p className="font-mono">{usdt(w.net_usdt)}</p></div>}
              </div>

              <div><p className="text-xs text-muted-foreground">Destination (TRC20)</p><p className="font-mono text-xs break-all bg-muted/50 rounded px-2 py-1.5">{w.destination_address}</p></div>

              {w.tx_hash && (
                <div><p className="text-xs text-muted-foreground">Transaction Hash</p>
                  <a href={explorer(w.tx_hash)} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1 font-mono text-xs break-all">{w.tx_hash}<ExternalLink className="h-3 w-3 shrink-0" /></a></div>
              )}
              {w.rejection_reason && <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-600"><b>Rejected:</b> {w.rejection_reason}</div>}

              {/* Action zones */}
              {isPending && (
                <div className="space-y-3 pt-2 border-t">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Rejection Reason (if rejecting)</Label>
                    <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason..." className="rounded-sm" data-testid="reject-reason" />
                  </div>
                  <div className="flex gap-2">
                    <Button className="flex-1 rounded-sm" disabled={busy} onClick={() => act('approve', {}, 'Withdrawal approved — balance deducted')} data-testid="approve-wd-btn">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" />Approve</>}
                    </Button>
                    <Button variant="destructive" className="flex-1 rounded-sm" disabled={busy || !reason} onClick={() => act('reject', { reason }, 'Withdrawal rejected — balance restored')} data-testid="reject-wd-btn">
                      <XCircle className="w-4 h-4 mr-2" />Reject
                    </Button>
                  </div>
                </div>
              )}

              {isApproved && (
                <div className="space-y-3 pt-2 border-t">
                  <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 text-xs text-blue-700">
                    Balance has been deducted. Perform the actual TRC20 transfer, then record the real transaction hash to mark it Completed.
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider flex items-center gap-1"><Hash className="h-3 w-3" />TRC20 Transaction Hash</Label>
                    <Input value={txHash} onChange={(e) => setTxHash(e.target.value)} placeholder="Real on-chain tx hash" className="rounded-sm font-mono text-xs" data-testid="tx-hash-input" />
                  </div>
                  <Button className="w-full rounded-sm" disabled={busy || txHash.trim().length < 10} onClick={() => act('tx-hash', { tx_hash: txHash.trim() }, 'Withdrawal completed')} data-testid="record-tx-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" />Record TX & Complete</>}
                  </Button>
                </div>
              )}
            </div>
          )}
          <SheetFooter><Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm w-full">Close</Button></SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
